import { FunctionComponent, useRef } from 'react';
import { DraftDesignConfiguration } from '@/types';
import { isPixelDesign } from '@/model/design/isDesignType';
import { MaxSizeHint } from '../../MaxSizeHint';
import { SnapLines } from '../../SnapLines';
import { useConfigurationTransform } from '../../hooks/useConfigurationTransform';
import { RendererProps } from '../ConfigurationRenderer';
import { ConfigurationHandlesWithZoom } from '../zoomConfigurationTest/ConfigurationHandlesWithZoom';
import { useDesign } from './useDesign';

export const DesignRenderer: FunctionComponent<RendererProps<DraftDesignConfiguration>> = ({
  configuration,
  viewMap,
  hasErrors,
  isSelected,
}) => {
  const designElementRef = useRef<SVGGElement | null>(null);

  const design = configuration.design;
  const isPixel = isPixelDesign(design);
  const minimumScale =
    !isPixel && design.restrictions && design.restrictions.minimumScale
      ? design.restrictions.minimumScale / 100
      : undefined;

  const {
    scale,
    maxScale,
    offset,
    rotation,
    infoText,
    isSnapTarget,
    onDelete,
    onMove,
    onPinch,
    onRotate,
    onScale,
    onSetSnapLineActive,
    snapLinesActive,
    showMaxSizeHint,
    snapLines,
  } = useConfigurationTransform(configuration, designElementRef, isSelected, isPixel, minimumScale);

  const [designElement, transform] = useDesign(
    configuration,
    designElementRef,
    offset,
    rotation,
    scale
  );

  return (
    <>
      <SnapLines viewMap={viewMap} lines={snapLines} />
      <g transform={transform}>
        {designElement}
        <ConfigurationHandlesWithZoom
          active={isSelected}
          configuration={configuration}
          isSnapTarget={isSnapTarget}
          infoText={infoText}
          onDelete={onDelete}
          onMove={onMove}
          onPinch={onPinch}
          onRotate={onRotate}
          onScale={onScale}
          scale={scale}
          viewMap={viewMap}
          onSetSnapLineActive={onSetSnapLineActive}
          snapLinesActive={snapLinesActive}
          hasConfigurationErrors={hasErrors}
        />
        <MaxSizeHint
          size={configuration.size}
          currentScale={scale}
          maxScale={maxScale}
          active={showMaxSizeHint}
        />
      </g>
    </>
  );
};
